import type { Client } from '@/lib/types';
import type { ClientDetail } from '@/context/clients-cache-context';
import { safeToDate } from '@/lib/utils';
import {
  computeShortClientClassification,
  computeShortClientClassificationFromSop,
} from './client-classification';
import logger from '@/lib/logging';

export type GetClientDetail = (clientId: string) => Promise<ClientDetail>;
export type UpdateClientFn = (clientId: string, data: Partial<Client>) => Promise<void>;
/**
 * Reports which client is actively being backfilled, so the roster can show an
 * honest "updating…" hint next to that client's row while its write is in
 * flight (rather than the classification badge silently changing on the next
 * poll with no explanation).
 */
export type BackfillProgress = (clientId: string, status: 'start' | 'done') => void;

/**
 * Backfill classification for clients missing it, fetching detail per client on demand.
 */
export async function backfillMissingClassifications(
  clients: Client[],
  getClientDetail: GetClientDetail,
  updateClient: UpdateClientFn,
  onProgress?: BackfillProgress
): Promise<void> {
  const toBackfill = clients.filter((c) => !c.classification);
  if (toBackfill.length === 0) return;

  for (const c of toBackfill) {
    onProgress?.(c.id, 'start');
    try {
      const detail = await getClientDetail(c.id);

      const clientAnalyses = [...detail.postureAnalyses].sort((a, b) => {
        const dateA = safeToDate(a.createdAt)?.getTime() || 0;
        const dateB = safeToDate(b.createdAt)?.getTime() || 0;
        return dateB - dateA;
      });

      if (clientAnalyses.length > 0) {
        const latest = clientAnalyses[0];
        const shortClass = await computeShortClientClassification(latest.analysis as never);
        if (shortClass) await updateClient(c.id, { classification: shortClass });
        continue;
      }

      const clientAssessments = [...detail.assessments].sort((a, b) => {
        const dateA = safeToDate(a.date)?.getTime() || 0;
        const dateB = safeToDate(b.date)?.getTime() || 0;
        return dateB - dateA;
      });

      if (clientAssessments.length > 0) {
        const latest = clientAssessments[0];
        const shortClass = await computeShortClientClassificationFromSop(
          latest as never,
          null,
          c.classification || ''
        );
        if (shortClass) await updateClient(c.id, { classification: shortClass });
      }
    } catch (e) {
      logger.warn(`Classification backfill failed for client ${c.id}: ${String(e)}`);
    } finally {
      onProgress?.(c.id, 'done');
    }
  }
}
